import * as React from "react";

import { runFloatingAsyncAction } from "../../../Helpers/TypingUtils";
import { PANEL_TAB_CHANGE } from "../../metricsAction";
import { EmbeddedLogContainer } from "../EmbeddedLog/EmbeddedLogContainer";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { ErrorLog } from "../../../Engine/Core/ErrorManager/Utils/Models";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";

import { Menu } from "./Menu/Menu";
import { PanelContent, PanelHeader, PanelRoot, PinContainer } from "./Panel.styles";
import { isPanelHidden, isPanelPinned, PanelTabType, PanelType } from "./PanelTypes";
import { Pin } from "./Pin/Pin";
import { PanelTabs } from "./Tabs/PanelTabs";
import { AttachmentNames } from "./FufAndAttachmentsUploadModal/FufAndAttachmentsUploadModalTypes";

export interface PanelProps {
    className?: string;
    errorLog: ErrorLog;
    hiddenConvertLink: boolean;
    isHideOrgName?: boolean;
    isPopupPanel: boolean;
    isWidePanel: boolean;
    panelState: PanelType;
    transferText?: string;
    errorLogWasRequested?: boolean;
    counterEnabled?: boolean;
    activeTab: PanelTabType;
    embeddedLogEnabled: boolean;
    modelChanged: boolean;
    isReadOnly?: boolean;
    canCheckByPopd?: boolean;
    uploadFufButtonEnabled: boolean;
    uploadFufAttachmentsButtonEnabled: boolean;
    uploadFufLightboxHideDemandNameButton: boolean;
    uploadFufLightboxTitle?: string;
    uploadFufLightboxAlert?: string;
    setHiddenOrgName: (x: boolean) => void;
    setPanelState: (x: PanelType) => void;
    setWidePanel: (x: boolean) => void;
    getErrorLog: () => Promise<void>;
    onChangeActiveTab: (nextValue: PanelTabType) => void;
    onChangeModelState: (modelChanged: boolean) => void;
    children: React.ReactNode;
    useExtendedErrorLog: boolean;
    fufSizeTooLargeErrorText?: string | React.ReactNode;
    getSverkaNdsPlusParams?: FormActionHandler;
    attachmentNames?: AttachmentNames;
}

export function Panel(props: PanelProps): JSX.Element {
    const track = useMetrics();

    const { embeddedLogEnabled, errorLog, isWidePanel, panelState, canCheckByPopd, activeTab } = props;
    const { errorsTotalCount = 0, warningsTotalCount = 0, isChecking, requestFailed } = errorLog;
    const [isHover, setIsHover] = React.useState(false);

    React.useEffect(() => {
        if (activeTab === "check" && !isChecking) {
            props.setWidePanel(true);
        }
    }, [isChecking, activeTab]);

    React.useEffect(() => {
        if (panelState === "show" && !props.isPopupPanel && !isHover) {
            props.setPanelState("hide");
        }
    }, [props.isPopupPanel]);

    const handleCheckErrors = (): void => {
        props.onChangeActiveTab("check");
        runFloatingAsyncAction(() => props.getErrorLog());
    };

    const handleTabChange = (value: PanelTabType): void => {
        track(PANEL_TAB_CHANGE, { tabState: value });
        if (value === "check" && !requestFailed) {
            if (isChecking === undefined) {
                runFloatingAsyncAction(() => props.getErrorLog());
            } else if (errorsTotalCount > 0 || warningsTotalCount > 0 || canCheckByPopd) {
                props.setWidePanel(true);
            }
        }
        if (value === "menu") {
            props.setWidePanel(false);
        }
        props.onChangeActiveTab(value);
    };

    const handleOnMouseEnterPanel = (): void => {
        if (isPanelHidden(panelState)) {
            props.setPanelState("show");
        }
    };

    const handleOnMouseLeavePanel = (): void => {
        setIsHover(false);
        if (panelState === "show" && !props.isPopupPanel) {
            props.setPanelState("hide");
        }
    };

    const counterCount = requestFailed ? 0 : errorsTotalCount + warningsTotalCount;

    const testPageObject = {
        getType: () => "Panel",
        getTab: () => props.activeTab,
        getErrors: () => errorsTotalCount,
        getWarnings: () => warningsTotalCount,
        getIsWide: () => isWidePanel,
        getRequestStatus: () => (requestFailed ? "failed" : "recieved"),
        toggle: async (active: "check" | "menu") => {
            handleTabChange(active);
        },
        check: handleCheckErrors,
        mouseEnter: handleOnMouseEnterPanel,
        mouseLeave: handleOnMouseLeavePanel,
        changeModelState: props.onChangeModelState,
    };

    return (
        <TestPageObjectHandle id={"Panel"} pageObject={testPageObject}>
            <PanelRoot
                wide={isWidePanel}
                shadow={!isPanelPinned(panelState)}
                className={props.className}
                onMouseEnter={handleOnMouseEnterPanel}
                onMouseLeave={handleOnMouseLeavePanel}>
                <PanelHeader embedded={embeddedLogEnabled} isReadOnly={props.isReadOnly}>
                    {embeddedLogEnabled && (
                        <PanelTabs
                            useExtendedErrorLog={props.useExtendedErrorLog}
                            active={props.activeTab}
                            counterCount={counterCount}
                            counterEnabled={Boolean(props.counterEnabled) && !isChecking}
                            onChange={handleTabChange}
                            isReadOnly={props.isReadOnly}
                        />
                    )}
                    <PinContainer>
                        <Pin
                            data-tid="AttachPinIcon"
                            expanded={isPanelPinned(panelState)}
                            pinned={panelState == "pinManual"}
                            onClick={() => props.setPanelState(isPanelPinned(panelState) ? "hideManual" : "pinManual")}
                        />
                    </PinContainer>
                </PanelHeader>
                <PanelContent>
                    {props.activeTab === "menu" ? (
                        <Menu
                            isHideOrgName={props.isHideOrgName}
                            checkErrors={handleCheckErrors}
                            hiddenConvertLink={props.hiddenConvertLink}
                            setHiddenOrgName={props.setHiddenOrgName}
                            setPanelState={props.setPanelState}
                            transferText={props.transferText}
                            uploadFufButtonEnabled={props.uploadFufButtonEnabled}
                            uploadFufAttachmentsButtonEnabled={props.uploadFufAttachmentsButtonEnabled}
                            fufSizeTooLargeErrorText={props.fufSizeTooLargeErrorText}
                            uploadFufLightboxHideDemandNameButton={props.uploadFufLightboxHideDemandNameButton}
                            uploadFufLightboxTitle={props.uploadFufLightboxTitle}
                            uploadFufLightboxAlert={props.uploadFufLightboxAlert}
                            attachmentNames={props.attachmentNames}>
                            {props.children}
                        </Menu>
                    ) : (
                        <EmbeddedLogContainer
                            onCheckErrors={handleCheckErrors}
                            errorLog={props.errorLog}
                            firstCheck={!props.errorLogWasRequested}
                            modelChanged={props.modelChanged}
                            getSverkaNdsPlusParams={props.getSverkaNdsPlusParams}
                        />
                    )}
                </PanelContent>
            </PanelRoot>
        </TestPageObjectHandle>
    );
}
